import React from 'react';

import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';

import { useSharingTools } from '../../../../hooks/useSharingTools';
import { type Platform, PLATFORM_CONFIG } from '../../../../shared/constants';
import { SharingToolsCardWrapperVariant1 } from '../../../SharingToolsCardWrapper';
import { ReadyToSharePostsCardLoading } from '../ReadyToSharePostsCardLoading';

type PostData = Readonly<{
	imageUrl: string;
	platform: Platform;
	title?: TranslationKey;
	description?: TranslationKey;
}>;

/**
 * Displays a preview card for ready-to-share social media posts.
 * Shows a loading state when no post data is provided.
 */
export const ReadyToSharePostsCardPreviewVariant1: React.FC<Props> = ({ post }) => {
	const {
		imageUrl,
		platform = 'instagram',
		title = translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.title'),
		description = translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.description'),
	} = post ?? {};

	const { actions } = useSharingTools();

	const { Icon, bgColor, bgGradient, iconColor, brandLabel } = PLATFORM_CONFIG[platform];

	return (
		<SharingToolsCardWrapperVariant1
			title={title}
			description={description}
			primaryActionOnClick={actions.navigateToReadyToSharePosts}
		>
			{post ? (
				<article
					data-platform={platform}
					data-testid={`ready-to-share-preview-${platform}`}
					className="relative h-[170px] w-[118px] flex-shrink-0"
					aria-label={`${brandLabel} ready-to-share post`}
				>
					<div
						className="h-full w-full rounded-lg bg-cover bg-center bg-no-repeat shadow-sm"
						style={{
							backgroundImage: `url(${imageUrl})`,
						}}
						role="img"
						aria-label="Post preview image"
					/>

					{/* Platform badge */}
					<div
						className={cn(
							'absolute -right-2 -top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full shadow-lg',
							bgGradient,
						)}
						style={!bgGradient ? { backgroundColor: bgColor } : undefined}
						data-testid={`${platform}-badge`}
						role="img"
						aria-label={`${brandLabel} platform`}
					>
						<Icon className={cn(iconColor, 'h-6 w-6')} size={24} aria-hidden="true" />
					</div>
				</article>
			) : (
				<ReadyToSharePostsCardLoading />
			)}
		</SharingToolsCardWrapperVariant1>
	);
};

type Props = Readonly<{
	post?: PostData;
}>;
